import { useState, useEffect } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { History, Trash2, Download, CheckCircle2, Loader2, AlertTriangle, Calendar, Clock } from "lucide-react";
import AppLogo from "@/components/AppLogo";

export default function HistoryPage() {
  const [logs, setLogs] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  
  // Filter state
  const [filter, setFilter] = useState("all"); // all, 7, 30, protocol
  const [selectedProtocol, setSelectedProtocol] = useState("");

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let params = new URLSearchParams();
      params.append("limit", "500");
      
      if (filter === "7") {
        params.append("days", "7");
      } else if (filter === "30") {
        params.append("days", "30");
      } else if (filter === "protocol" && selectedProtocol) {
        params.append("protocol_id", selectedProtocol);
      }
      
      const res = await api.get(`/dose-logs?${params.toString()}`);
      setLogs(res.data);
    } catch (error) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const fetchProtocols = async () => {
    try {
      const res = await api.get("/protocols");
      setProtocols(res.data);
    } catch (error) {
      console.error("Failed to load protocols");
    }
  };

  useEffect(() => {
    fetchProtocols();
  }, []);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedProtocol]);

  const handleDeleteLog = async (logId) => {
    try {
      await api.delete(`/dose-logs/${logId}`);
      toast.success("Entry deleted");
      setLogs(logs.filter(l => l.id !== logId));
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get("/dose-logs/export", {
        responseType: "blob"
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `labstack-history-${format(new Date(), "yyyy-MM-dd")}.csv`;
      
      // Append to body (needed for Firefox)
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("History exported");
    } catch (error) {
      toast.error("Failed to export history");
    } finally {
      setExporting(false);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      await api.delete("/dose-logs/clear");
      toast.success("All history cleared");
      setLogs([]);
      setShowClearDialog(false);
    } catch (error) {
      toast.error("Failed to clear history");
    } finally {
      setClearing(false);
    }
  };

  // Group logs by date
  const groupedLogs = logs.reduce((groups, log) => {
    const date = log.completed_at.split("T")[0];
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(log);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedLogs).sort((a, b) => b.localeCompare(a));

  return (
    <div className="page-container" data-testid="history-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <AppLogo size="small" showText={false} />
            <h1 className="font-heading text-lg font-bold">History</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || logs.length === 0}
              className="h-9 rounded-full"
              data-testid="export-history-btn"
            >
              {exporting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <Download size={16} className="mr-1" />
                  Export
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowClearDialog(true)}
              disabled={logs.length === 0}
              className="h-9 rounded-full text-destructive hover:text-destructive"
              data-testid="clear-history-btn"
            >
              <Trash2 size={16} className="mr-1" />
              Clear
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
            className="h-8 rounded-full whitespace-nowrap"
            data-testid="filter-all"
          >
            All
          </Button>
          <Button
            variant={filter === "7" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("7")}
            className="h-8 rounded-full whitespace-nowrap"
            data-testid="filter-7days"
          >
            Last 7 Days
          </Button>
          <Button
            variant={filter === "30" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("30")}
            className="h-8 rounded-full whitespace-nowrap"
            data-testid="filter-30days"
          >
            Last 30 Days
          </Button>
          <Select 
            value={filter === "protocol" ? selectedProtocol : ""} 
            onValueChange={(val) => {
              setFilter("protocol");
              setSelectedProtocol(val);
            }}
          >
            <SelectTrigger 
              className={`h-8 w-auto min-w-[120px] rounded-full ${filter === "protocol" ? "bg-primary text-primary-foreground" : ""}`}
              data-testid="filter-protocol"
            >
              <SelectValue placeholder="By Protocol" />
            </SelectTrigger>
            <SelectContent>
              {protocols.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.compound_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 space-y-4">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
              <p className="text-muted-foreground font-medium">No history yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Logged doses will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          sortedDates.map((date, dateIndex) => (
            <div key={date} className="space-y-3 animate-fadeIn" style={{ animationDelay: `${dateIndex * 0.05}s`, opacity: 0 }}>
              {/* Date Header */}
              <div className="flex items-center gap-2 sticky top-[120px] z-30 bg-background/80 backdrop-blur py-2">
                <Calendar size={16} className="text-primary" />
                <h2 className="font-semibold text-sm">
                  {format(parseISO(date), "EEEE, MMMM d, yyyy")}
                </h2>
                <span className="text-xs text-muted-foreground ml-auto">
                  {groupedLogs[date].length} {groupedLogs[date].length === 1 ? 'entry' : 'entries'}
                </span>
              </div>

              {/* Log Entries */}
              {groupedLogs[date].map((log, logIndex) => (
                <Card 
                  key={log.id} 
                  className="overflow-hidden"
                  data-testid={`history-entry-${dateIndex}-${logIndex}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={20} className="text-green-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold">{log.compound_name}</h3>
                            <p className="text-sm text-primary font-medium">
                              {log.dose} {log.dose_unit}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteLog(log.id)}
                            className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive flex-shrink-0"
                            data-testid={`delete-entry-${dateIndex}-${logIndex}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        
                        <div className="mt-2 space-y-1">
                          {log.protocol_name && (
                            <p className="text-xs text-muted-foreground">
                              Protocol: {log.protocol_name}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {log.scheduled_time && (
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                Scheduled: {log.scheduled_time}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <CheckCircle2 size={12} />
                              Logged: {format(parseISO(log.completed_at), "HH:mm")}
                            </span>
                          </div>
                        </div>
                        
                        {log.notes && (
                          <p className="text-xs text-muted-foreground italic mt-2">
                            {log.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ))
        )}
      </main>

      {/* Clear All Confirmation Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete All History?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {logs.length} history entries. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAll}
              disabled={clearing}
              className="bg-destructive hover:bg-destructive/90"
            >
              {clearing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete All"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
